import React, { useRef, useState } from "react";
import { Smile, Shirt, Footprints, Drumstick, LayoutGrid } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { handshakeRain } from "../../lib/hotdogRain";
import { HandshakeIcon } from "../HandshakeIcon";

export const SLOT_META = {
  all:    { label: "All",    color: "#FFFFFF", alt: "#C9BDF0", icon: LayoutGrid },
  face:   { label: "Face",   color: "#FFE600", alt: "#FF9500", icon: Smile,      tag: "Masks & Paint" },
  jacket: { label: "Jacket", color: "#FF007F", alt: "#9D00FF", icon: Shirt,      tag: "Ring Jackets" },
  boots:  { label: "Boots",  color: "#00F0FF", alt: "#4D9FFF", icon: Footprints, tag: "Stompers" },
  food:   { label: "Food",   color: "#39FF14", alt: "#B8FF00", icon: Drumstick,  tag: "Gains Fuel" },
};

export function SlotChip({ id, active, count, onClick, testPrefix = "shop-slot" }) {
  const m = SLOT_META[id];
  const Icon = m.icon;
  return (
    <button data-testid={`${testPrefix}-${id}`} onClick={onClick}
            className="shop-chip group flex items-center gap-1.5 px-3.5 py-2 rounded-full font-heading uppercase tracking-widest text-[11px] border-2 transition-[transform,background-color,box-shadow,color] duration-200 hover:-translate-y-0.5"
            style={{ background: active ? `linear-gradient(90deg, ${m.color}, ${m.alt})` : "#0A051ACC", color: active ? "#0A051A" : m.color,
                     borderColor: active ? m.color : `${m.color}66`, boxShadow: active ? `0 0 16px ${m.color}88` : "none" }}>
      <Icon size={13} strokeWidth={2.6} className="transition-transform group-hover:rotate-12" />
      {m.label}
      <span className="lb-num text-[10px] opacity-75">({count})</span>
    </button>
  );
}

export function InfoPill({ testId, color, title, label, children }) {
  return (
    <div data-testid={testId} title={title}
         className="px-3.5 py-2 rounded-full font-heading uppercase tracking-widest text-[11px] border-2 bg-[#0A051A]/90 backdrop-blur"
         style={{ color, borderColor: color, boxShadow: `0 0 12px ${color}55` }}>
      {label} <span className="lb-num font-bold ml-1">{children}</span>
    </div>
  );
}

export function CategoryHeader({ id, count }) {
  const m = SLOT_META[id];
  const Icon = m.icon;
  return (
    <div className="flex items-center gap-3 mb-3 mt-1" data-testid={`shop-category-${id}`}>
      <div className="w-9 h-9 rounded-xl flex items-center justify-center border-2" style={{ borderColor: m.color, background: `${m.color}1F`, boxShadow: `0 0 14px ${m.color}66` }}>
        <Icon size={18} strokeWidth={2.6} style={{ color: m.color }} />
      </div>
      <div className="min-w-0">
        <div className="font-graffiti text-2xl leading-none" style={{ color: m.color, textShadow: `0 0 12px ${m.color}88, 2px 2px 0 #000` }}>{m.label}</div>
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#A89BCC]">{m.tag} · <span className="lb-num">{count}</span></div>
      </div>
      <div className="h-[2px] flex-1 rounded-full" style={{ background: `linear-gradient(90deg, ${m.color}, ${m.alt}55, transparent)` }} />
    </div>
  );
}

/* Gradient-framed shop card shell; slot colour drives the identity. */
export function ShopFrame({ testId, color, alt, dim, celebrating, delay = 0, className = "", children }) {
  return (
    <div data-testid={testId}
         className={`shop-card wr-rise group relative rounded-2xl p-[2px] transition-transform duration-300 hover:-translate-y-1 ${celebrating ? "scale-[1.03]" : ""} ${className}`}
         style={{ background: `linear-gradient(150deg, ${color}, ${alt}88, ${color}55)`, "--shop-glow": color, animationDelay: `${delay}ms`,
                  boxShadow: celebrating ? `0 0 44px ${color}, 0 0 80px ${color}66` : undefined }}>
      <div className="relative h-full rounded-[14px] overflow-hidden" style={{ background: `linear-gradient(160deg, ${color}26, #110726F5 45%, #0A051A)`, opacity: dim ? 0.72 : undefined }}>
        <div className="pointer-events-none absolute -top-10 -right-10 w-32 h-32 rounded-full blur-2xl opacity-25 transition-opacity duration-300 group-hover:opacity-55" style={{ background: color }} />
        {children}
      </div>
    </div>
  );
}

export function SecretHandshake() {
  const { refresh } = useAuth();
  const ref = useRef(null);
  const [state, setState] = useState("idle");
  const click = async () => {
    if (state === "busy") return;
    setState("busy");
    try {
      const r = await api.post("/shop/secret-handshake");
      if (r.data?.ok) {
        await handshakeRain(ref.current?.getBoundingClientRect(), { count: 1, burst: 1.6, duration: 1100 });
        await refresh();
        setState("found");
      } else {
        setState("nope");
        setTimeout(() => setState("idle"), 600);
      }
    } catch {
      setState("idle");
    }
  };
  return (
    <button ref={ref} type="button" onClick={click} data-testid="secret-handshake-btn" aria-label="Handshake"
            className={`relative inline-flex items-center justify-center w-11 h-11 rounded-xl border-2 border-[#00F0FF]/70 bg-[#00F0FF]/10 transition-transform duration-200 hover:scale-110 active:scale-90 ${state === "nope" ? "secret-hs-wiggle" : ""}`}
            style={{ boxShadow: "0 0 16px #00F0FF55" }}>
      <HandshakeIcon className="w-6 h-6" />
    </button>
  );
}
